import React from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { ICON_MAP } from './ConversationIcons';

const PillSelector = ({
  value,
  options,
  onChange,
  isActive = false,
  testId,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const selectedOption = options.find(opt => opt.value === value);
  const SelectedIcon = ICON_MAP[value];

  return (
    <div className="relative" data-testid={testId}>
      {/* Pill trigger */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => setIsOpen(!isOpen)}
        className={`
          px-5 py-3 rounded-2xl flex items-center gap-2.5 transition-[border-color,box-shadow,background-color,opacity] duration-300 focus:outline-none
          ${isActive
            ? 'border-2 border-orange-primary shadow-lg shadow-orange-primary/10'
            : 'border border-charcoal/10 shadow-md shadow-charcoal/5'
          }
        `}
        style={{
          background: 'linear-gradient(145deg, rgba(255,252,248,0.95), rgba(255,245,235,0.9))',
        }}
      >
        {SelectedIcon && (
          <span className="text-charcoal/55">
            <SelectedIcon />
          </span>
        )}
        <span className="text-charcoal font-medium">
          {selectedOption?.label || value}
        </span>
        <ChevronDown
          size={15}
          className={`text-charcoal/40 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </motion.button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-full left-0 mb-2 z-50 min-w-full"
          >
            <div
              className="rounded-xl overflow-hidden shadow-xl border border-charcoal/10"
              style={{
                background: 'linear-gradient(145deg, rgba(255,252,248,0.98), rgba(255,245,235,0.95))',
                backdropFilter: 'blur(12px)',
              }}
            >
              {options.map((option) => {
                const Icon = ICON_MAP[option.value];
                const isSelected = option.value === value;
                return (
                  <button
                    key={option.value}
                    onClick={() => { onChange(option.value); setIsOpen(false); }}
                    className={`
                      w-full px-4 py-3 text-left flex items-center gap-3
                      transition-[background-color,color] duration-200 hover:bg-orange-primary/10
                      ${isSelected ? 'bg-orange-primary/12 text-orange-primary' : 'text-charcoal'}
                    `}
                    data-testid={`${testId}-option-${option.value}`}
                  >
                    {Icon && (
                      <span className={isSelected ? 'text-orange-primary' : 'text-charcoal/50'}>
                        <Icon />
                      </span>
                    )}
                    <span>
                      <div className="font-medium leading-tight">{option.label}</div>
                      {option.description && (
                        <div className="text-xs opacity-50 mt-0.5">{option.description}</div>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        </>
      )}
    </div>
  );
};

export default PillSelector;
